import type { Diagram } from '@/types/EMRALD_Model';
import { Box, CircularProgress, Typography } from '@mui/material';
import { signal } from '@preact/signals';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PiDotsNine } from 'react-icons/pi';
import { TbMap } from 'react-icons/tb';
import ReactFlow, {
  Background,
  BackgroundVariant,
  ControlButton,
  Controls,
  MiniMap,
} from 'reactflow';
import { DialogComponent } from '@/components/common/DialogComponent/DialogComponent';
import { DownloadButton } from '@/components/diagrams/DownloadButton';
import { StateNode } from '@/components/diagrams/EmraldDiagram/StateNodeComponent';
import { ContextMenu } from '@/components/layout/ContextMenu/ContextMenu';
import { emptyDiagram } from '@/contexts/DiagramContext';
import { useWindowContext } from '@/contexts/WindowContext';
import { CustomConnectionLine } from './Edges/ConnectionLineComponent';
import { useContextMenu } from './useContextMenu';
import { useEmraldDiagram } from './useEmraldDiagram';
import 'reactflow/dist/style.css';

interface EmraldDiagramProps {
  diagram: Diagram;
}

// The currently focused diagram. Driven by the active window (see the effect
// below) rather than by render, so it stays correct when several diagrams are
// open. Writing it on every render let a non-focused diagram's re-render
// clobber it, which pointed edits at the wrong diagram.
export const currentDiagram = signal(emptyDiagram);

export const EmraldDiagram: React.FC<EmraldDiagramProps> = ({ diagram }) => {
  const [showMap, setShowMap] = useState(true);
  const [showBackgroundDots, setShowBackgroundDots] = useState(true);
  const { activeWindowId, getWindowTitleById } = useWindowContext();
  const {
    nodes,
    edges,
    loading,
    setEdges,
    getStateNodes,
    onNodesChange,
    onEdgesChange,
    onEdgeClick,
    onPaneClick,
    onNodeDragStop,
    onConnect,
    onNodeDoubleClick,
    onEdgeUpdate,
    isValidConnection,
    setTopDiagram,
  } = useEmraldDiagram();
  const {
    menu,
    menuOptions,
    deleteConfirmation,
    itemToDelete,
    deleteItem,
    closeDeleteConfirmation,
    closeContextMenu,
    onPaneContextMenu,
    onNodeContextMenu,
    onEdgeContextMenu,
  } = useContextMenu(getStateNodes, setEdges, diagram);

  useEffect(() => {
    setTopDiagram(diagram);
  }, []);

  // Only the diagram whose window is active is the "current" one. A diagram
  // window's title is its name, so when this instance's window is focused we
  // publish it to the shared signal. Non-focused diagrams leave it untouched.
  useEffect(() => {
    if (getWindowTitleById(activeWindowId) === diagram.name) {
      currentDiagram.value = diagram;
    }
  }, [activeWindowId, diagram]);
  const nodeTypes = useMemo(() => ({ custom: StateNode }), []);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {loading ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <h4>Loading Diagram</h4>
        </Box>
      ) : (
        <div
          className="emrald-diagram"
          ref={ref}
          style={{ width: '100%', height: '100%' }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onEdgeClick={onEdgeClick}
            onConnect={onConnect}
            onPaneContextMenu={onPaneContextMenu}
            onNodeContextMenu={onNodeContextMenu}
            onNodeDoubleClick={onNodeDoubleClick}
            onEdgeContextMenu={(event, edge) => {
              onEdgeContextMenu(event, edge, edges);
            }}
            onEdgeUpdate={onEdgeUpdate}
            onPaneClick={() => {
              closeContextMenu();
              onPaneClick();
            }}
            connectionLineComponent={CustomConnectionLine}
            onNodeDragStop={onNodeDragStop}
            isValidConnection={isValidConnection}
            proOptions={{ hideAttribution: true }}
            fitView
            nodeTypes={nodeTypes}
          >
            <Controls>
              <ControlButton
                onClick={() => {
                  setShowMap(!showMap);
                }}
              >
                <TbMap />
              </ControlButton>
              <ControlButton
                onClick={() => {
                  setShowBackgroundDots(!showBackgroundDots);
                }}
              >
                <PiDotsNine />
              </ControlButton>
              <DownloadButton diagramName={diagram.name} />
            </Controls>
            {showMap && <MiniMap pannable />}
            {showBackgroundDots && (
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            )}
          </ReactFlow>
          {menu && (
            <ContextMenu
              mouseX={menu.mouseX}
              mouseY={menu.mouseY}
              handleClose={closeContextMenu}
              options={menuOptions}
            />
          )}
          {deleteConfirmation && (
            <DialogComponent
              open={true}
              title="Delete Confirmation"
              submitText="delete"
              onSubmit={() => {
                deleteItem();
              }}
              onClose={() => {
                closeDeleteConfirmation();
              }}
            >
              <Typography>
                Are you sure you want to delete {itemToDelete?.name}? It will be
                removed from all other places it is used.
              </Typography>
            </DialogComponent>
          )}
        </div>
      )}
    </Box>
  );
};
